import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Phone, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PHONE_RX = /^\+?[0-9][0-9\s-]{8,18}[0-9]$/;
const digits = (v) => (v || '').replace(/\D/g, '').replace(/^0+/, '');
const phoneOk = (v) => PHONE_RX.test((v || '').trim()) && digits(v).length >= 10 && digits(v).length <= 15;

const Field = ({ label, hint, error, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-gray-700">{label}</span>
    <div className="mt-1">{children}</div>
    {error ? <span className="text-[11px] text-red-600">{error}</span> : hint && <span className="text-[11px] text-gray-500">{hint}</span>}
  </label>
);
const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200';

// Super Admin: phone + WhatsApp shown in the site footer and Contact page (live, no redeploy).
export const SiteContactTab = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/super/site-contact`, { headers })
      .then(({ data }) => { setForm(data.settings); setMeta(data); })
      .catch(() => toast.error('Failed to load contact settings'));
  }, []);
  if (!form) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const errs = {
    phone: phoneOk(form.phone) ? '' : 'Enter 10–15 digits (spaces, dashes and a leading + allowed)',
    whatsapp_number: !form.whatsapp_number || phoneOk(form.whatsapp_number) ? '' : 'Enter 10–15 digits, or leave empty to use the phone number',
    whatsapp_message: form.whatsapp_message?.trim() && form.whatsapp_message.length <= 500 ? '' : 'Message must be 1–500 characters',
    whatsapp_channel_url: !form.whatsapp_channel_url || /^https:\/\/(whatsapp\.com\/channel\/|chat\.whatsapp\.com\/)[A-Za-z0-9_-]{6,}$/.test(form.whatsapp_channel_url.trim()) ? '' : 'Use a link like https://whatsapp.com/channel/…',
  };
  const invalid = Object.values(errs).some(Boolean);
  const wa = digits(form.whatsapp_number || form.phone);
  const waLink = `https://wa.me/${wa.length === 10 ? `91${wa}` : wa}?text=${encodeURIComponent(form.whatsapp_message || '')}`;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/admin/super/site-contact`, form, { headers });
      setForm(data.settings);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success('Contact details updated — live on the website now');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl bg-white border border-gray-200 rounded-xl p-6 space-y-5" data-testid="site-contact-tab">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Phone size={18} /> Website contact & WhatsApp</h2>
        <p className="text-sm text-gray-500">Shown in the site footer and the Contact Us page. Changes go live immediately.</p>
        {meta.updated_at && <p className="text-[11px] text-gray-400 mt-1" data-testid="site-contact-updated">Last updated {new Date(meta.updated_at).toLocaleString()}{meta.updated_by ? ` by ${meta.updated_by}` : ''}</p>}
      </div>
      <Field label="Support phone number" error={errs.phone}>
        <input className={input} value={form.phone} onChange={set('phone')} placeholder="+91 92343 54174" data-testid="site-contact-phone" />
      </Field>
      <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
        <input type="checkbox" checked={!!form.whatsapp_enabled} onChange={set('whatsapp_enabled')} data-testid="site-contact-wa-enabled" /> Show "Chat on WhatsApp" button
      </label>
      <Field label="WhatsApp number" hint="Leave empty to use the support phone number. 10-digit numbers get +91 automatically." error={errs.whatsapp_number}>
        <input className={input} value={form.whatsapp_number} onChange={set('whatsapp_number')} placeholder={form.phone} data-testid="site-contact-wa-number" />
      </Field>
      <Field label="Prefilled WhatsApp message" error={errs.whatsapp_message}>
        <textarea className={input} rows={2} maxLength={500} value={form.whatsapp_message} onChange={set('whatsapp_message')} data-testid="site-contact-wa-message" />
      </Field>
      <Field label="WhatsApp Channel link (optional)" hint="Leave empty to hide the channel link." error={errs.whatsapp_channel_url}>
        <input className={input} value={form.whatsapp_channel_url} onChange={set('whatsapp_channel_url')} placeholder="https://whatsapp.com/channel/…" data-testid="site-contact-wa-channel" />
      </Field>
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button type="button" onClick={save} disabled={saving || invalid} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="site-contact-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        {wa && !errs.whatsapp_number && !errs.phone && (
          <a href={waLink} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#128C7E] hover:underline" data-testid="site-contact-wa-test">Test WhatsApp link ↗</a>
        )}
      </div>
    </div>
  );
};
